import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Home } from "./pages/Home";
import { useEffect, lazy, Suspense } from "react";

// Lazy load non-critical pages
const Applications = lazy(() => import("./pages/Applications").then(m => ({ default: m.Applications })));
const Login = lazy(() => import("./pages/Login").then(m => ({ default: m.Login })));
const Register = lazy(() => import("./pages/Register").then(m => ({ default: m.Register })));
const Dashboard = lazy(() => import("./pages/Dashboard").then(m => ({ default: m.Dashboard })));
const AdminPanel = lazy(() => import("./pages/AdminPanel").then(m => ({ default: m.AdminPanel })));
const Pricing = lazy(() => import("./pages/Pricing").then(m => ({ default: m.Pricing })));
const Subscriptions = lazy(() => import("./pages/Subscriptions").then(m => ({ default: m.Subscriptions })));
const About = lazy(() => import("./pages/About").then(m => ({ default: m.About })));
const Contact = lazy(() => import("./pages/Contact").then(m => ({ default: m.Contact })));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword").then(m => ({ default: m.ForgotPassword })));
const Setup = lazy(() => import("./pages/Setup").then(m => ({ default: m.Setup })));
const Settings = lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
const SSOLogin = lazy(() => import("./pages/SSOLogin").then(m => ({ default: m.SSOLogin })));
const AppDetail = lazy(() => import("./pages/AppDetail").then(m => ({ default: m.AppDetail })));
const DistanceSalesAgreement = lazy(() => import("./pages/DistanceSalesAgreement").then(m => ({ default: m.DistanceSalesAgreement })));
const CancellationRefund = lazy(() => import("./pages/CancellationRefund").then(m => ({ default: m.CancellationRefund })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const FAQ = lazy(() => import("./pages/FAQ").then(m => ({ default: m.FAQ })));
const HowItWorks = lazy(() => import("./pages/HowItWorks").then(m => ({ default: m.HowItWorks })));
const Checkout = lazy(() => import("./pages/Checkout").then(m => ({ default: m.Checkout })));

const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
    </div>
);

// Preload both logos at app start to prevent flicker on theme switch
const preloadImages = () => {
    const dark = new Image(); dark.src = '/logo.png?v=4';
    const light = new Image(); light.src = '/logo-light.png?v=4';
};

const AppToaster = () => {
    const { isDark } = useTheme();
    return (
        <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
                style: isDark ? {
                    background: 'hsl(0, 0%, 11%)',
                    border: '1px solid hsl(40, 20%, 18%)',
                    color: '#F5E9CE'
                } : {
                    background: '#ffffff',
                    border: '1px solid hsl(40, 15%, 85%)',
                    color: 'hsl(0, 0%, 11%)'
                }
            }}
        />
    );
};

// Google Analytics sayfa goruntulemesi (SPA uyumu)
const GAPageTracker = () => {
    const location = useLocation();
    useEffect(() => {
        if (window.gtag) {
            window.gtag('event', 'page_view', {
                page_path: location.pathname + location.search,
                page_title: document.title
            });
        }
    }, [location]);
    return null;
};

// Inner pages (non-landing) overlay - ensures solid bg
const InnerPageOverlay = () => {
    const { isDark } = useTheme();
    const location = useLocation();
    const landingPaths = ['/', '/hakkimizda', '/iletisim', '/fiyatlandirma', '/uygulamalar', '/sso-login', '/mesafeli-satis-sozlesmesi', '/iptal-iade', '/gizlilik-politikasi', '/odeme', '/sss', '/uygulama', '/nasil-calisir'];
    const isLanding = landingPaths.includes(location.pathname);
    if (isLanding) return null;
    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
            backgroundColor: isDark ? '#060606' : '#FFFFFF' }} />
    );
};

// Solid background layer (theme-aware) - no image
const BackgroundLayer = () => {
    const { isDark } = useTheme();
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 0,
                backgroundColor: isDark ? '#060606' : '#FFFFFF',
                pointerEvents: 'none',
            }}
        />
    );
};

function App() {
    useEffect(() => { preloadImages(); }, []);
    return (
        <HelmetProvider>
        <ThemeProvider>
            <AuthProvider>
                <div className="App min-h-screen flex flex-col relative">
                    <BackgroundLayer />
                    <BrowserRouter>
                        <GAPageTracker />
                        <InnerPageOverlay />
                        <Navbar />
                        <main className="flex-1">
                            <Suspense fallback={<PageLoader />}>
                            <Routes>
                                <Route path="/" element={<Home />} />
                                <Route path="/uygulamalar" element={<Applications />} />
                                <Route path="/fiyatlandirma" element={<Pricing />} />
                                <Route path="/hakkimizda" element={<About />} />
                                <Route path="/iletisim" element={<Contact />} />
                                <Route path="/uygulama/:slug" element={<AppDetail />} />
                                <Route path="/mesafeli-satis-sozlesmesi" element={<DistanceSalesAgreement />} />
                                <Route path="/iptal-iade" element={<CancellationRefund />} />
                                <Route path="/gizlilik-politikasi" element={<PrivacyPolicy />} />
                                <Route path="/odeme" element={<Checkout />} />
                                <Route path="/sss" element={<FAQ />} />
                                <Route path="/nasil-calisir" element={<HowItWorks />} />
                                <Route path="/sso-login" element={<SSOLogin />} />
                                <Route path="/giris" element={<Login />} />
                                <Route path="/kayit" element={<Register />} />
                                <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
                                <Route path="/kurulum" element={<Setup />} />
                                <Route 
                                    path="/dashboard" 
                                    element={
                                        <ProtectedRoute>
                                            <Dashboard />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route 
                                    path="/aboneliklerim" 
                                    element={
                                        <ProtectedRoute>
                                            <Subscriptions />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route 
                                    path="/admin" 
                                    element={
                                        <ProtectedRoute adminOnly>
                                            <AdminPanel />
                                        </ProtectedRoute>
                                    } 
                                />
                                <Route 
                                    path="/ayarlar" 
                                    element={
                                        <ProtectedRoute>
                                            <Settings />
                                        </ProtectedRoute>
                                    } 
                                />
                            </Routes>
                            </Suspense>
                        </main>
                        <Footer />
                    </BrowserRouter>
                    <AppToaster />
                </div>
            </AuthProvider>
        </ThemeProvider>
        </HelmetProvider>
    );
}

export default App;
